import * as THREE from 'three'

import { resolveScreenImageIdAtGlobalTime } from '@/animation/resolveScreenImageIdAtGlobalTime'
import {
  SCREEN_IMAGE_TRANSITION_NONE,
  SCREEN_RASTER_TRANSITION_TIME_JUMP_THRESHOLD_SEC,
  screenImageTransitionDurationSec,
  type ScreenImageTransitionModeId,
} from '@/constants/screenImageTransition'
import { effectiveScreenRasterObjectUrl } from '@/rendering/screenTexture/effectiveScreenRasterUrl'
import { SCREEN_USERDATA } from '@/rendering/screenTexture/screenMaterialRuntime'
import {
  ensureScreenTransitionPatchOnMesh,
  resetScreenTransitionUniforms,
  writeScreenTransitionUniforms,
} from '@/rendering/screenTexture/screenStandardMaterialTransition'
import type { AnimationLayer } from '@/types/animationLayer'

/**
 * 多图栅格切换 GPU 过渡：始终保持 `MeshStandardMaterial`，仅通过 `onBeforeCompile` 注入的
 * `(uTransitionMapTo, uTransitionMix, uTransitionMode)` 三个 uniform 控制混合。
 * 过渡进行中、完成后与无过渡时共用同一条 PBR 光照管线，色调完全一致。
 */
export const SCREEN_RASTER_TRANS_USERDATA_KEY = 'screenRasterTransState'

type TransPhase = 'idle' | 'loading_target' | 'animating'

type ScreenRasterTransState = {
  lastEvalTime: number
  committedUrl: string
  committedRid: string
  phase: TransPhase
  tStart: number
  activeMode: ScreenImageTransitionModeId
  toUrl: string
  toRid: string
  /** 加载完成后的目标贴图（commit 时移交给 `screenMap` 不再 dispose 于此） */
  toTex: THREE.Texture | null
  queuedUrl: string | null
  queuedRid: string | null
  pendingLoadGen: number
}

function createInitialState(): ScreenRasterTransState {
  return {
    lastEvalTime: Number.NaN,
    committedUrl: '',
    committedRid: '',
    phase: 'idle',
    tStart: 0,
    activeMode: SCREEN_IMAGE_TRANSITION_NONE,
    toUrl: '',
    toRid: '',
    toTex: null,
    queuedUrl: null,
    queuedRid: null,
    pendingLoadGen: 0,
  }
}

function getOrInitState(group: THREE.Group): ScreenRasterTransState {
  const ex = group.userData[SCREEN_RASTER_TRANS_USERDATA_KEY] as ScreenRasterTransState | undefined
  if (ex) return ex
  const st = createInitialState()
  group.userData[SCREEN_RASTER_TRANS_USERDATA_KEY] = st
  return st
}

/**
 * 复位过渡运行时：清 transition uniforms 与等待中的目标贴图，**不**替换屏幕材质实例
 * （材质始终是 `MeshStandardMaterial`，仅 uniform 归零等价于无过渡状态）。
 *
 * @param group - 图层 Group
 */
export function disposeScreenRasterTransitionState(group: THREE.Group): void {
  const st = group.userData[SCREEN_RASTER_TRANS_USERDATA_KEY] as ScreenRasterTransState | undefined
  const screenMesh = group.userData.screenMesh as THREE.Mesh | undefined
  if (st) {
    st.pendingLoadGen += 1
    if (st.toTex) {
      st.toTex.dispose()
      st.toTex = null
    }
    st.phase = 'idle'
    st.queuedUrl = null
    st.queuedRid = null
  }
  if (screenMesh) {
    resetScreenTransitionUniforms(screenMesh)
  }
  delete group.userData[SCREEN_RASTER_TRANS_USERDATA_KEY]
}

function configureRasterTexture(tex: THREE.Texture): void {
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.generateMipmaps = true
  tex.needsUpdate = true
}

export type ScreenRasterTransitionCallbacks = {
  isImagePlane: boolean
  imagePlaneOpacity: number
  releaseUserScreenTextures: (group: THREE.Group) => void
  bindImagePlane: (mesh: THREE.Mesh, tex: THREE.Texture) => void
  bindDeviceScreen: (mesh: THREE.Mesh, tex: THREE.Texture) => void
  onImagePlaneRasterNaturalSize: (group: THREE.Group, srcUrl: string) => void
}

/**
 * 将新加载的栅格贴图绑定为 `map` / `emissiveMap`，记录 `screenMap` 引用并 dispose 旧引用。
 *
 * @param group - 图层 Group
 * @param screenMesh - 屏幕网格
 * @param tex - 已配置色彩空间的目标贴图
 * @param url - 该贴图的 blob URL（用于 `lastAppliedRasterSrc` 与 image-plane 自适应回调）
 * @param cbs - 回调集
 */
function commitNewMap(
  group: THREE.Group,
  screenMesh: THREE.Mesh,
  tex: THREE.Texture,
  url: string,
  cbs: ScreenRasterTransitionCallbacks,
): void {
  const prevTex = group.userData[SCREEN_USERDATA.screenMap] as THREE.Texture | undefined
  if (cbs.isImagePlane) {
    cbs.bindImagePlane(screenMesh, tex)
    cbs.onImagePlaneRasterNaturalSize(group, url)
  } else {
    cbs.bindDeviceScreen(screenMesh, tex)
  }
  group.userData[SCREEN_USERDATA.screenMap] = tex
  group.userData.lastAppliedRasterSrc = url
  if (prevTex && prevTex !== tex) {
    prevTex.dispose()
  }
}

/**
 * 校验加载结果是否仍对应当前 group 的屏幕网格；不匹配则 dispose 并返回 null。
 */
function pickAcceptedScreenMesh(
  group: THREE.Group,
  screenMesh: THREE.Mesh,
  expectedLoadId: number,
  loadedTex: THREE.Texture,
): THREE.Mesh | null {
  if (group.userData.textureLoadId !== expectedLoadId) {
    loadedTex.dispose()
    return null
  }
  const sm = group.userData.screenMesh as THREE.Mesh | undefined
  if (!sm || sm !== screenMesh) {
    loadedTex.dispose()
    return null
  }
  return sm
}

/**
 * 瞬时切换：先 patch + 清 uniform，再异步加载新贴图，最后绑定并 dispose 旧贴图。
 * 加载前不释放 `screenMap`，避免「贴图清空 → 等待新贴图就绪」的白屏闪烁。
 * 若旧路径仍是 `<video>`，必须先完整释放（与原视频 → 栅格语义一致）。
 *
 * @param group - 图层 Group
 * @param screenMesh - 屏幕网格
 * @param url - 目标 blob URL
 * @param rid - 目标 raster id（多图栈语义键）
 * @param cbs - 回调集
 */
function instantLoadRasterUrl(
  group: THREE.Group,
  screenMesh: THREE.Mesh,
  url: string,
  rid: string,
  cbs: ScreenRasterTransitionCallbacks,
): void {
  const loadId = ((group.userData.textureLoadId as number | undefined) ?? 0) + 1
  group.userData.textureLoadId = loadId
  if (group.userData[SCREEN_USERDATA.screenVideoEl]) {
    cbs.releaseUserScreenTextures(group)
  }
  const loader = new THREE.TextureLoader()
  loader.load(
    url,
    (tex) => {
      const sm = pickAcceptedScreenMesh(group, screenMesh, loadId, tex)
      if (!sm) return
      configureRasterTexture(tex)
      ensureScreenTransitionPatchOnMesh(sm)
      resetScreenTransitionUniforms(sm)
      commitNewMap(group, sm, tex, url, cbs)
      const stNow = getOrInitState(group)
      stNow.committedUrl = url
      stNow.committedRid = rid
      stNow.phase = 'idle'
      if (stNow.toTex) {
        stNow.toTex.dispose()
        stNow.toTex = null
      }
    },
    undefined,
    () => {},
  )
}

/**
 * 兼容占位：opacity 不再走 ShaderMaterial，改由 `applyImagePlaneScreenOpacity` 直接写 `MeshStandardMaterial.opacity`。
 * 保留导出符号以便外层无缝过渡。
 */
export function setScreenRasterShaderOpacityIfPresent(_screenMesh: THREE.Mesh, _opacity: number): void {
  /* no-op：保留 API 形参占位 */
}

/**
 * 主入口：根据当前全局时间决策瞬时切换或推进过渡。
 *
 * @param group - 图层 Group
 * @param layer - 图层数据
 * @param screenMesh - 屏幕网格
 * @param globalTimeSec - 全局时间（秒）
 * @param transitionMode - 当前过渡模式（含 `NONE`）
 * @param cbs - 回调集
 */
export function syncScreenRasterTextureWithTransition(
  group: THREE.Group,
  layer: AnimationLayer,
  screenMesh: THREE.Mesh,
  globalTimeSec: number,
  transitionMode: ScreenImageTransitionModeId,
  cbs: ScreenRasterTransitionCallbacks,
): void {
  const T = Number(globalTimeSec)
  const desiredUrl = effectiveScreenRasterObjectUrl(layer, T)
  const desiredRid =
    layer.screenMediaType === 'image' && layer.screenImages.length > 0
      ? resolveScreenImageIdAtGlobalTime(layer, T) ?? ''
      : ''

  const texKeyStable = `${desiredUrl ?? ''}\0${layer.screenMediaType}\0${desiredRid}`
  const stackCount = layer.screenImages.length

  let st = getOrInitState(group)

  const jumped =
    Number.isFinite(st.lastEvalTime) &&
    Math.abs(T - st.lastEvalTime) > SCREEN_RASTER_TRANSITION_TIME_JUMP_THRESHOLD_SEC
  if (jumped) {
    disposeScreenRasterTransitionState(group)
    st = getOrInitState(group)
  }
  st.lastEvalTime = T

  if (!desiredUrl || layer.screenMediaType !== 'image') {
    disposeScreenRasterTransitionState(group)
    group.userData.appliedTextureKey = texKeyStable
    return
  }

  const useGpu =
    stackCount >= 2 && transitionMode !== SCREEN_IMAGE_TRANSITION_NONE && Boolean(desiredUrl)

  if (!useGpu) {
    disposeScreenRasterTransitionState(group)
    if (group.userData.appliedTextureKey === texKeyStable) return
    group.userData.appliedTextureKey = texKeyStable
    instantLoadRasterUrl(group, screenMesh, desiredUrl, desiredRid, cbs)
    return
  }

  if (st.phase === 'animating') {
    advanceAnimatingPhase(group, layer, screenMesh, st, T, transitionMode, cbs)
    return
  }

  if (st.phase === 'loading_target') {
    return
  }

  if (desiredUrl === st.committedUrl && desiredRid === st.committedRid) {
    group.userData.appliedTextureKey = texKeyStable
    return
  }

  startTransitionToTarget({
    group,
    layer,
    screenMesh,
    st,
    targetUrl: desiredUrl,
    targetRid: desiredRid,
    transitionMode,
    T,
    cbs,
  })
}

/**
 * 推进 `animating` 阶段：写入混合权重；若达终点则 commit 新贴图并尝试启动 queue。
 */
function advanceAnimatingPhase(
  group: THREE.Group,
  layer: AnimationLayer,
  screenMesh: THREE.Mesh,
  st: ScreenRasterTransState,
  T: number,
  transitionMode: ScreenImageTransitionModeId,
  cbs: ScreenRasterTransitionCallbacks,
): void {
  const dur = screenImageTransitionDurationSec(st.activeMode)
  const u = dur <= 1e-6 ? 1 : Math.min(1, Math.max(0, (T - st.tStart) / dur))
  writeScreenTransitionUniforms(screenMesh, st.toTex, u, Math.max(0, st.activeMode))
  if (u < 1 - 1e-5 || !st.toTex) return

  const finishedTex = st.toTex
  st.toTex = null
  commitNewMap(group, screenMesh, finishedTex, st.toUrl, cbs)
  resetScreenTransitionUniforms(screenMesh)
  st.committedUrl = st.toUrl
  st.committedRid = st.toRid
  st.phase = 'idle'
  group.userData.appliedTextureKey = `${st.committedUrl}\0${layer.screenMediaType}\0${st.committedRid}`

  if (!st.queuedUrl) return
  const qUrl = st.queuedUrl
  const qRid = st.queuedRid ?? ''
  st.queuedUrl = null
  st.queuedRid = null
  startTransitionToTarget({
    group,
    layer,
    screenMesh,
    st,
    targetUrl: qUrl,
    targetRid: qRid,
    transitionMode,
    T,
    cbs,
  })
}

type StartTransitionRequest = {
  group: THREE.Group
  layer: AnimationLayer
  screenMesh: THREE.Mesh
  st: ScreenRasterTransState
  targetUrl: string
  targetRid: string
  transitionMode: ScreenImageTransitionModeId
  T: number
  cbs: ScreenRasterTransitionCallbacks
}

/**
 * 启动新的目标过渡：异步加载目标贴图，加载完成后将 uniform 切换到 `animating`。
 * 进行中再次调用走 queue 路径（在主入口处理）。
 */
function startTransitionToTarget(req: StartTransitionRequest): void {
  const { group, layer, screenMesh, st, targetUrl, targetRid, transitionMode, T, cbs } = req
  const fromTex = group.userData[SCREEN_USERDATA.screenMap] as THREE.Texture | undefined
  if (!st.committedUrl || !fromTex) {
    group.userData.appliedTextureKey = ''
    instantLoadRasterUrl(group, screenMesh, targetUrl, targetRid, cbs)
    return
  }
  if (targetUrl === st.committedUrl && targetRid === st.committedRid) {
    group.userData.appliedTextureKey = `${targetUrl}\0${layer.screenMediaType}\0${targetRid}`
    return
  }

  ensureScreenTransitionPatchOnMesh(screenMesh)

  st.phase = 'loading_target'
  st.activeMode = transitionMode
  st.toUrl = targetUrl
  st.toRid = targetRid
  st.pendingLoadGen += 1
  const gen = st.pendingLoadGen

  const loader = new THREE.TextureLoader()
  loader.load(
    targetUrl,
    (toTex) => {
      if (st.pendingLoadGen !== gen) {
        toTex.dispose()
        return
      }
      const sm = group.userData.screenMesh as THREE.Mesh | undefined
      if (!sm || sm !== screenMesh) {
        toTex.dispose()
        return
      }
      configureRasterTexture(toTex)
      st.toTex = toTex
      st.tStart = T
      st.phase = 'animating'
      writeScreenTransitionUniforms(sm, toTex, 0, Math.max(0, transitionMode))
      group.userData.appliedTextureKey = `${st.committedUrl}\0${layer.screenMediaType}\0${st.committedRid}\0__trans__${targetUrl}`
    },
    undefined,
    () => {
      st.phase = 'idle'
      st.toUrl = ''
      st.toRid = ''
    },
  )
}
